/**
 * CSS selector for the table editor wrapper element.
 * Used by paste hooks to detect whether focus is inside a table editor.
 * Must match the `data-table-editor` attribute set on the outer div in TestTableEditor.
 */
export const TABLE_EDITOR_SELECTOR = '[data-table-editor]';

export type ClipboardSource =
    | 'google-sheets'
    | 'excel'
    | 'excel-online'
    | 'google-docs'
    | 'word'
    | 'unknown';

const SOURCE_LABELS: Record<ClipboardSource, string> = {
    'google-sheets': 'Google Sheets',
    excel: 'Microsoft Excel',
    'excel-online': 'Excel Online',
    'google-docs': 'Google Docs',
    word: 'Microsoft Word',
    unknown: 'Área de transferência',
};

/**
 * Detects the origin application of clipboard data by inspecting the `text/html`
 * content for application-specific fingerprints.
 *
 * Excel, Google Sheets, and Google Docs each inject distinctive markers into the
 * HTML they place on the clipboard. There is no standard API for reading the source
 * application, so content-based heuristics are the only reliable approach.
 *
 * Takes the raw HTML rather than the event: the context-menu paste path reads the
 * clipboard through `navigator.clipboard.read()` and has no `ClipboardEvent`.
 */
export const detectClipboardSource = (html: string): ClipboardSource => {
    if (!html) return 'unknown';

    if (html.includes('<google-sheets-html-origin>') || html.includes('data-sheets-value')) {
        return 'google-sheets';
    }

    if (
        html.includes('urn:schemas-microsoft-com:office:excel') ||
        html.includes('"ProgId" content="Excel.Sheet"')
    ) {
        return 'excel';
    }

    if (html.includes('data-ccp-timestamp')) {
        return 'excel-online';
    }

    if (html.includes('docs-internal-guid')) {
        return 'google-docs';
    }

    if (
        html.includes('urn:schemas-microsoft-com:office:word') ||
        html.includes('content="Word.Document"') ||
        html.includes('Generator content="Microsoft Word')
    ) {
        return 'word';
    }

    return 'unknown';
};

/**
 * Returns a human-readable label for the detected clipboard source.
 */
export const getSourceLabel = (source: ClipboardSource): string => {
    return SOURCE_LABELS[source];
};

/**
 * Parses tab-separated values (TSV) from clipboard text.
 * Excel and Google Sheets copy cells as TSV in the text/plain format.
 *
 * Limitation: does not handle quoted fields. If an Excel cell contains a tab
 * or newline, Excel wraps the value in double quotes — this parser will split
 * incorrectly in that edge case.
 *
 * @returns A 2D array of strings (rows × columns).
 */
export const parseClipboardTSV = (text: string): string[][] => {
    if (!text) return [];

    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

    // Remove trailing empty line (clipboard often adds a trailing newline)
    if (lines.length > 0 && lines[lines.length - 1] === '') {
        lines.pop();
    }

    if (lines.length === 0) return [];

    return lines.map((line) => line.split('\t'));
};

/**
 * Returns true when the grid holds more than one cell.
 *
 * This is a shape question, not a "should this become a table" question — it
 * decides whether an HTML `<table>` is substantial enough to beat the TSV
 * fallback, and whether a cell-level paste is worth previewing. Use
 * {@link isTabularData} to decide whether clipboard content becomes a table
 * block.
 */
export const hasMultipleCells = (parsed: string[][]): boolean => {
    if (parsed.length === 0) return false;
    if (parsed.length > 1) return true;
    // Single row with multiple columns is still more than one cell
    return parsed[0].length > 1;
};

/** A parsed grid plus where it came from. See {@link parseClipboardGrid}. */
export type ClipboardGrid = {
    grid: string[][];
    origin: 'html-table' | 'tsv';
} | null;

const hasContent = (grid: string[][]): boolean =>
    grid.some((row) => row.some((cell) => cell.trim() !== ''));

/**
 * Returns true when clipboard content is genuinely tabular and should become a
 * table block.
 *
 * Provenance is the whole point: a real one-column `<table>` and three lines of
 * prose both parse to `[['a'], ['b'], ['c']]`, so the grid alone cannot tell
 * them apart. An actual `<table>` qualifies at any shape; plain text only when
 * it is tab-delimited. Multi-line prose is not a one-column table.
 */
export const isTabularData = (parsed: ClipboardGrid): boolean => {
    if (!parsed || !hasContent(parsed.grid)) return false;
    if (parsed.origin === 'html-table') return true;
    return parsed.grid.some((row) => row.length > 1);
};

/**
 * Flattens a cell's text: Docs/Word wrap cell content in <p>/<span>/<br> and mso
 * markup, so textContent carries stray newlines, tabs, and non-breaking spaces.
 */
const normalizeCellText = (raw: string): string =>
    raw
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

/**
 * Parses the first meaningful `<table>` from clipboard HTML into a 2D grid.
 *
 * Unlike TSV parsing, this reads the authoritative table structure that every
 * source (Excel, Google Sheets, Google Docs, Word) places in `text/html`. It is
 * the only path that works for Google Docs / Word, whose `text/plain` does not
 * reliably tab-separate cells.
 *
 * The HTML is parsed into an inert document (no script execution, no resource
 * loading) and we only read `textContent`, so no sanitization is required.
 *
 * Known limitation: `rowspan` is not reconstructed — a vertically merged cell's
 * value stays in its declared row and lower rows shift left. `colspan` is
 * expanded with empty cells to preserve column alignment.
 *
 * @returns A 2D array of strings (rows × columns), or `null` when no table is found.
 */
export const parseClipboardHtmlTable = (html: string): string[][] | null => {
    if (!html) return null;

    let doc: Document;
    try {
        doc = new DOMParser().parseFromString(html, 'text/html');
    } catch {
        return null;
    }

    const tables = Array.from(doc.querySelectorAll('table'));
    if (tables.length === 0) return null;

    // Word wraps real tables inside layout tables; pick the one with the most cells.
    const table = tables.reduce((best, candidate) =>
        candidate.querySelectorAll('td, th').length > best.querySelectorAll('td, th').length
            ? candidate
            : best,
    );

    const grid: string[][] = [];

    for (const tr of Array.from(table.querySelectorAll('tr'))) {
        // Skip rows owned by a nested table (querySelectorAll descends into them).
        if (tr.closest('table') !== table) continue;

        // Only direct-child cells, so a nested table's cells don't leak as columns.
        const cells = Array.from(tr.children).filter(
            (el) => el.tagName === 'TD' || el.tagName === 'TH',
        );
        if (cells.length === 0) continue;

        const rowValues: string[] = [];
        for (const cell of cells) {
            rowValues.push(normalizeCellText(cell.textContent ?? ''));

            const colSpan = Math.max(
                1,
                Number.parseInt(cell.getAttribute('colspan') || '1', 10) || 1,
            );
            for (let extra = 1; extra < colSpan; extra++) {
                rowValues.push('');
            }
        }

        grid.push(rowValues);
    }

    // Drop fully-empty trailing rows (Docs/Word often append blank rows).
    while (grid.length > 0 && grid[grid.length - 1].every((cell) => cell === '')) {
        grid.pop();
    }

    return grid.length > 0 ? grid : null;
};

/**
 * Parses a clipboard payload into a 2D grid, preferring the authoritative HTML
 * `<table>` (works for every source) and falling back to TSV from plain text
 * (Excel/Sheets legacy path) when no usable HTML table is present.
 *
 * Keeps the origin so callers can tell a real `<table>` from tab-split text —
 * see {@link isTabularData}.
 */
export const parseClipboardGrid = (
    html: string | undefined,
    plainText: string | undefined,
): ClipboardGrid => {
    const fromHtml = html ? parseClipboardHtmlTable(html) : null;
    if (fromHtml && hasMultipleCells(fromHtml)) return { grid: fromHtml, origin: 'html-table' };

    const tsv = parseClipboardTSV(plainText ?? '');
    return tsv.length > 0 ? { grid: tsv, origin: 'tsv' } : null;
};

/** {@link parseClipboardGrid} without the origin, for callers that only need cells. */
export const parseClipboardTable = (
    html: string | undefined,
    plainText: string | undefined,
): string[][] => parseClipboardGrid(html, plainText)?.grid ?? [];
